import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter,
  ArrowRight,
  Sparkles,
  Zap,
  Loader2,
  Grid3X3,
  ChevronRight
} from 'lucide-react';
import Button from '../../components/Button';
import { mockupService } from '../../services/mockupService';
import { getCategoryIcon, getCategoryStyle, CategoryDisplayNames } from '../../components/TemplateIcons';

interface Template {
  id: string;
  name: string;
  category: string;
  description: string;
}

const Mockups: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Load templates from backend
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await mockupService.getTemplates();
      if (response.success) {
        setTemplates(response.templates);
        setCategories(response.categories);
        console.log('Loaded templates:', response.templates);
        console.log('Categories:', response.categories);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      // Fallback to default templates
      setTemplates([
        { id: 'book-hardcover', name: 'Hardcover Book', category: 'books', description: 'Premium hardcover book mockup' },
        { id: 'book-paperback', name: 'Paperback Book', category: 'books', description: 'Standard paperback book mockup' },
        { id: 'tshirt-front', name: 'T-Shirt Front', category: 'apparel', description: 'T-shirt front view mockup' },
        { id: 'hoodie', name: 'Hoodie', category: 'apparel', description: 'Comfortable hoodie mockup' },
        { id: 'business-card', name: 'Business Card', category: 'stationery', description: 'Professional business card' },
        { id: 'poster-wall', name: 'Wall Poster', category: 'stationery', description: 'Poster on wall mockup' },
        { id: 'software-box', name: 'Software Box', category: 'packaging', description: '3D software box packaging' },
        { id: 'shopping-bag-paper', name: 'Shopping Bag', category: 'packaging', description: 'Paper shopping bag' },
        { id: 'laptop-screen', name: 'Laptop Screen', category: 'digital', description: 'Website on laptop' },
        { id: 'smartphone-screen', name: 'Phone Screen', category: 'digital', description: 'App on smartphone' },
        { id: 'billboard-city', name: 'City Billboard', category: 'outdoor', description: 'Urban billboard mockup' },
        { id: 'vehicle-wrap-van', name: 'Van Wrap', category: 'outdoor', description: 'Delivery van branding' },
        { id: 'mug-ceramic', name: 'Coffee Mug', category: 'merchandise', description: 'Ceramic coffee mug' },
        { id: 'water-bottle-metal', name: 'Water Bottle', category: 'merchandise', description: 'Metal water bottle' }
      ]);
      setCategories(['books', 'apparel', 'stationery', 'packaging', 'digital', 'outdoor', 'merchandise', 'publishing', 'food-beverage', 'home-office', 'events']);
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleSelectTemplate = (templateId: string) => {
    console.log('Selected template:', templateId);
    
    // Clear any previous session data
    sessionStorage.clear();
    
    // Store selected template
    sessionStorage.setItem('selectedTemplate', templateId);
    sessionStorage.setItem('creationStep', '1');
    
    console.log('Navigating to create page...');
    
    // Navigate to create page (step 2)
    navigate('/dashboard/create');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-brand-purple" />
      </div>
    );
  }

  // Featured templates for hero section
  const featuredCategories = ['books', 'apparel', 'digital', 'merchandise'];
  const featuredTemplates = templates.filter(t => 
    featuredCategories.includes(t.category) && 
    ['book-hardcover', 'tshirt-front', 'laptop-screen', 'mug-ceramic'].includes(t.id)
  );

  return (
    <div className="min-h-screen">
      {/* Container with proper padding and max-width */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10 py-8">
        {/* Header Section with improved spacing */}
        <div className="mb-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="space-y-2">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
                Choose Your Template
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl">
                Select from our extensive library of professional mockup templates to showcase your designs
              </p>
            </div>
            
            <Button
              variant="secondary"
              size="medium"
              onClick={() => navigate('/dashboard')}
              className="self-start sm:self-auto"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>

        {/* Hero Section with Featured Templates */}
        {selectedCategory === 'all' && searchQuery === '' && (
          <div className="bg-gradient-to-br from-brand-purple/5 via-brand-blue/5 to-transparent rounded-3xl p-8 sm:p-10 mb-12 border border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-brand-purple/10 rounded-lg">
                <Sparkles className="w-6 h-6 text-brand-purple" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900">Popular Templates</h2>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredTemplates.map((template) => {
              const categoryStyle = getCategoryStyle(template.category);
              const CategoryIcon = getCategoryIcon(template.category);
              
              return (
                <div
                  key={template.id}
                  onClick={() => handleSelectTemplate(template.id)}
                  className={`
                    relative overflow-hidden rounded-xl p-6 cursor-pointer
                    bg-gradient-to-br ${categoryStyle.gradientFrom} ${categoryStyle.gradientTo}
                    transform transition-all duration-300 hover:scale-105 hover:shadow-xl
                    group
                  `}
                >
                  <div className="relative z-10">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center mb-4">
                      <CategoryIcon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-white font-semibold text-lg mb-1">{template.name}</h3>
                    <p className="text-white/80 text-sm">{template.description}</p>
                    <ArrowRight className="w-5 h-5 text-white/60 absolute bottom-4 right-4 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                  
                  {/* Background decoration */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl" />
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full blur-2xl" />
                </div>
              );
            })}
          </div>
        </div>
      )}

        {/* Search and Filters Section */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Bar */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search templates by name or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-6 py-4 bg-white border border-gray-200 rounded-xl text-base placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-purple/20 focus:border-brand-purple transition-all duration-200 shadow-sm hover:shadow-md"
                />
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-3 bg-gray-100 p-1 rounded-lg self-start lg:self-auto">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  viewMode === 'grid' 
                    ? 'bg-white text-brand-purple shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Grid3X3 className="w-4 h-4" />
                <span className="hidden sm:inline">Grid</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  viewMode === 'list' 
                    ? 'bg-white text-brand-purple shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
          </div>
        </div>

        {/* Category Pills */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Categories</h3>
            <span className="text-sm text-gray-400">
              {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} available
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`
                px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200
                ${selectedCategory === 'all' 
                  ? 'bg-brand-purple text-white shadow-lg shadow-brand-purple/25 transform scale-105' 
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:shadow-md'
                }
              `}
            >
              All Templates
            </button>
            
            {categories.map((cat) => {
              const categoryStyle = getCategoryStyle(cat);
              const CategoryIcon = getCategoryIcon(cat);
              const isSelected = selectedCategory === cat;
              
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`
                    px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200
                    flex items-center gap-2.5 group
                    ${isSelected 
                      ? `${categoryStyle.bgColor} ${categoryStyle.color} shadow-lg transform scale-105` 
                      : `bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:shadow-md`
                    }
                  `}
                >
                  <CategoryIcon className={`w-4 h-4 ${isSelected ? '' : 'group-hover:scale-110 transition-transform'}`} />
                  {CategoryDisplayNames[cat] || cat}
                </button>
              );
            })}
          </div>
        </div>

        {/* Templates Grid/List */}
        <div className={viewMode === 'grid' 
          ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" 
          : "space-y-4"
        }>
        {filteredTemplates.map((template) => {
          const categoryStyle = getCategoryStyle(template.category);
          const CategoryIcon = getCategoryIcon(template.category);
          
          if (viewMode === 'list') {
            return (
              <div
                key={template.id}
                onClick={() => handleSelectTemplate(template.id)}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:border-brand-purple hover:shadow-xl transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-5">
                    <div className={`w-14 h-14 ${categoryStyle.bgColor} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <CategoryIcon className={`w-7 h-7 ${categoryStyle.color}`} />
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-semibold text-lg text-gray-900 group-hover:text-brand-purple transition-colors">
                        {template.name}
                      </h3>
                      <p className="text-sm text-gray-600">{template.description}</p>
                      <span className={`inline-flex text-xs font-medium ${categoryStyle.color} ${categoryStyle.bgColor} px-2.5 py-0.5 rounded-full`}>
                        {CategoryDisplayNames[template.category] || template.category}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="secondary"
                      size="small"
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      onClick={() => handleSelectTemplate(template.id)}
                    >
                      Use Template
                    </Button>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-brand-purple group-hover:translate-x-1 transition-all duration-200" />
                  </div>
                </div>
              </div>
            );
          }
          
          return (
            <div
              key={template.id}
              onClick={() => handleSelectTemplate(template.id)}
              className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-brand-purple hover:shadow-2xl transition-all duration-300 cursor-pointer group transform hover:-translate-y-1"
            >
              {/* Template Preview Area */}
              <div className={`h-56 ${categoryStyle.bgColor} relative overflow-hidden`}>
                <div className="absolute inset-0 flex items-center justify-center">
                  <CategoryIcon className={`w-24 h-24 ${categoryStyle.color} opacity-20 group-hover:scale-110 transition-transform duration-300`} />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                
                {/* Category Badge */}
                <div className={`absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-medium bg-white/90 backdrop-blur-sm text-gray-700 shadow-lg`}>
                  {CategoryDisplayNames[template.category] || template.category}
                </div>
                
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-brand-purple/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                  <Button
                    variant="primary"
                    size="small"
                    className="w-full bg-white text-brand-purple hover:bg-gray-50"
                    onClick={() => handleSelectTemplate(template.id)}
                  >
                    Use This Template
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-semibold text-lg text-gray-900 group-hover:text-brand-purple transition-colors mb-2">
                  {template.name}
                </h3>
                <p className="text-sm text-gray-600 line-clamp-2">
                  {template.description}
                </p>
                
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 ${categoryStyle.bgColor} rounded-lg flex items-center justify-center`}>
                      <CategoryIcon className={`w-4 h-4 ${categoryStyle.color}`} />
                    </div>
                    <span className="text-xs text-gray-500 font-medium">
                      {CategoryDisplayNames[template.category] || template.category}
                    </span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-brand-purple group-hover:translate-x-1 transition-all duration-200" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

        {/* Empty State */}
        {filteredTemplates.length === 0 && (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Search className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">No templates found</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              We couldn't find any templates matching your criteria. Try adjusting your search or filters.
            </p>
            <Button
              variant="secondary"
              size="medium"
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              className="shadow-sm hover:shadow-md"
            >
              Clear All Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Mockups;

